import classNames from 'classnames'
import React, { useState } from 'react'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { LinkSection } from '../../types/LinkSection'
import { PortableTextBodyElement } from '../../utilities/blockContent'
import { Breakpoints } from '../../utilities/breakpoints'
import { fitBoxesInColumns, getSizedBoxes } from '../../utilities/footerLayout'
import { IS_EQT_FOUNDATION } from '../../utilities/site'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Grid } from '../Grid'
import { Logo } from '../Logo'
import { NewsletterForm } from '../NewsletterForm/NewsletterForm'
import { SignupFormBlock, SignupFormBlockProps } from '../SignupForm/SignupFormBlock'
import { FooterColumn, FooterColumnProps } from './FooterColumn'
import { FooterDisclosure } from './FooterDisclosure'

export interface FooterProps {
  copyrightInfo?: string
  disclosure?: PortableTextBodyElement[]
  hideSignupForm?: boolean
  items?: LinkSection[]
  signupFormProps?: SignupFormBlockProps
  tagLine?: string
}

export const Footer = ({
  copyrightInfo,
  disclosure,
  hideSignupForm = false,
  items = [],
  signupFormProps,
  tagLine,
}: FooterProps): JSX.Element => {
  const tabletColumnCount = 5
  const mediumDesktopColumnCount = 8
  const [aboveSmall, setAboveSmall] = useState<boolean>(false)
  useBreakpoint(Breakpoints.md, setAboveSmall)

  const mobileColumns: FooterColumnProps[] = [
    {
      boxes: items.filter(
        box => box.hideSubItemsInMobile && !isNullOrWhiteSpace(box.url)
      ),
      collapsed: true,
      additionalClassName: 'col-start-1 col-span-1 pl-15 tb:hidden',
    },
    {
      boxes: items.filter(box => !box.hideSubItemsInMobile),
      collapsed: false,
      additionalClassName: 'col-start-2 col-span-1 pr-15 tb:hidden',
    },
  ]

  // divide the boxes into 5 or 8 columns depending on screen size
  const numColumns = aboveSmall ? mediumDesktopColumnCount : tabletColumnCount
  const sizedBoxes = getSizedBoxes(items)
  const organizedColumns = fitBoxesInColumns(numColumns, sizedBoxes, true)
  const isFooterFull = organizedColumns.length === numColumns

  const desktopColumns: FooterColumnProps[] = organizedColumns.map(col => ({
    boxes: col.boxes.map(box => box.rootNode),
  }))

  return (
    <footer
      className={classNames('bg-site-background pb-60', 'sm:pb-90', 'lg:pb-120', {
        'border-t border-neutral-lighter pt-60 sm:pt-90 lg:pt-120': IS_EQT_FOUNDATION,
      })}
    >
      {disclosure && (
        <FooterDisclosure
          additionalClassName="mb-60 sm:mb-90 lg:mb-120"
          body={disclosure}
        />
      )}

      {!hideSignupForm &&
        signupFormProps &&
        (signupFormProps.useNewDesign ? (
          <NewsletterForm
            {...signupFormProps}
            additionalClassName="mb-60 sm:mb-90 lg:mb-120"
          />
        ) : (
          <SignupFormBlock
            {...signupFormProps}
            additionalClassName="mb-60 sm:mb-90 lg:mb-120"
          />
        ))}

      <Grid additionalClassName="tb:min-h-120" contentAlignment="justify-center">
        <div
          className={classNames(
            'hidden',
            'tb:block tb:col-span-full',
            'sm:col-start-2 sm:col-span-4',
            'md:col-span-full md:-mx-30',
            'lg:col-start-2 lg:col-span-6',
            'xl:col-start-2 xl:col-span-8 xl:mx-0',
            'xxl:col-start-2 xxl:col-span-12'
          )}
        >
          <div
            className={classNames(
              'flex items-start -mr-15',
              isFooterFull ? 'justify-between' : 'justify-evenly'
            )}
          >
            {desktopColumns.map((col, i) => (
              <FooterColumn
                {...col}
                additionalClassName="mr-15"
                key={`col-${i}`}
                maxWidth="max-w-150 xl:max-w-240"
              />
            ))}
          </div>
        </div>

        {mobileColumns.map((col, i) => (
          <FooterColumn key={`col-${i}`} {...col} />
        ))}
      </Grid>

      <div className="mt-75 tb:mt-15 mx-auto max-w-240 tb:max-w-[37.5rem] text-center text-secondary-darker">
        <Logo className="inline-block mb-30 text-primary fill-current" />

        {(!isNullOrWhiteSpace(tagLine) || !isNullOrWhiteSpace(copyrightInfo)) && (
          <div className="flex flex-col gap-15 tb:gap-5">
            {!isNullOrWhiteSpace(tagLine) && (
              <div className="font-footer-slogan text-footer-slogan">{tagLine}</div>
            )}

            {!isNullOrWhiteSpace(copyrightInfo) && (
              <small className="font-footer-copyright text-footer-copyright">
                {copyrightInfo}
              </small>
            )}
          </div>
        )}
      </div>
    </footer>
  )
}
